import { Injectable } from '@angular/core';
import { ViewScopeService } from './view-scope.service';

export interface WorkbenchItem 
{
	documentId: number;
	journalID: number;
	isChecked: boolean;
}

export type LastViewed = { documentId?: number; journalId?: number }; // For last viewed document highlight

@Injectable({
	providedIn: 'root'
})
export class WorkbenchIntegratorService 
{

	private readonly lastViewedID  = "DJLV"; // Document & Journal Last Viewed.
	private readonly storageNameID = "DJWS"; // Document & Journal Workbench Storage. Also called as CodeName.
	readonly defaultChecked = true;
	readonly pseudoJournalId = -1; // Filler journal id for documents with no real journals; never send to movein.

	constructor(private viewScopeService: ViewScopeService) 
	{
		this.ensureStorageNameIDCreatedInLocalSession(); // Set the object in the sessionStorage.
		this.ensureLastViewedIDCreatedInLocalSession();  // Set the object in the sessionStorage.
		if (!this.isUserIdInCodeName()) this.insertId(); // If userId is not in, create it.
	}


	/**
	 * Checks if the storageNameID key exists in localStorage.
	 */
	isStorageNameCreatedInLocalSession(): boolean 
	{
		return localStorage.getItem(this.storageNameID) !== null;
	}

	/**
	 * Checks if the lastViewedID key exists in localStorage.
	 */
	isLastViewedCreatedInLocalSession() : boolean 
	{
		return localStorage.getItem(this.lastViewedID) !== null;
	}


	/**
	 * Creates the storageNameID key in localStorage if it does not exist.
	 */
	ensureStorageNameIDCreatedInLocalSession(): void 
	{
		if (!this.isStorageNameCreatedInLocalSession()) localStorage.setItem(this.storageNameID, JSON.stringify({}));
	}


	/**
	 * Creates the lastViewedID key in localStorage if it does not exist.
	 */
	ensureLastViewedIDCreatedInLocalSession(): void 
	{
		if (!this.isLastViewedCreatedInLocalSession()) localStorage.setItem(this.lastViewedID, JSON.stringify({}));
	}



	/**
	 * Checks if the given userId exists as a key inside the storageNameID object in localStorage.
	 */
	isUserIdInCodeName(): boolean {
		const raw = localStorage.getItem(this.storageNameID);
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!raw) return false;
		try {
			const obj = JSON.parse(raw);
			return obj.hasOwnProperty(userId);
		}
		catch {
			return false;
		}
	}



	/**
	 * Adds the current customer id as a key to the storageNameID object in localStorage if not present.
	 * The value will be an empty object by default.
	 */
	insertId(): void {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return;
		let obj: any = {};
		const raw = localStorage.getItem(this.storageNameID);
		if (raw) {
			try {
				obj = JSON.parse(raw);
			}
			catch {
				obj = {};
			}
		}
		if (!obj.hasOwnProperty(userId)) {
			obj[userId] = {};
			localStorage.setItem(this.storageNameID, JSON.stringify(obj));
		}
	}



	/**
	 * Updates the isChecked status for a document or journal for the current user in localStorage.
	 * If isDoc is true, updates all journals under the documentId; otherwise, updates the specific journalId.
	 */
	changeStatus(isDoc: boolean, id: number, statusToSet: boolean): void {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return;
		const raw = localStorage.getItem(this.storageNameID);
		if (!raw) return;
		let obj: any;
		try {
			obj = JSON.parse(raw);
		} catch {
			return;
		}
		if (!obj[userId]) return;

		if (isDoc) {
			// id is documentId, update all journals under this document
			const docKey = id.toString();
			if (obj[userId][docKey]) {
				Object.keys(obj[userId][docKey]).forEach(journalId => {
					obj[userId][docKey][journalId] = statusToSet;
				});
			}
		} else {
			// id is journalId, find the document containing this journal and update it
			Object.keys(obj[userId]).forEach(docKey => {
				if (obj[userId][docKey] && obj[userId][docKey].hasOwnProperty(id.toString())) {
					obj[userId][docKey][id.toString()] = statusToSet;
				}
			});
		}
		localStorage.setItem(this.storageNameID, JSON.stringify(obj));
	}

	/**
	 * Sets the checked status for every journal stored for the current customer.
	 * This updates the full DJWS tree, leaving the document structure intact.
	 */
	setAllJournalsStatus(statusToSet: boolean): void {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return;
		const raw = localStorage.getItem(this.storageNameID);
		if (!raw) return;

		let obj: any;
		try {
			obj = JSON.parse(raw);
		} catch {
			return;
		}

		if (!obj[userId] || typeof obj[userId] !== 'object') return;

		Object.keys(obj[userId]).forEach(docKey => {
			const journals = obj[userId][docKey];
			if (!journals || typeof journals !== 'object') return;

			Object.keys(journals).forEach(journalKey => {
				journals[journalKey] = statusToSet;
			});
		});

		localStorage.setItem(this.storageNameID, JSON.stringify(obj));
	}

	/**
	 * Returns true if the document has some but not all journals selected (for the current user).
	 * Returns false if all are selected, all are deselected, or there are no journals.
	 */
	isDocumentPartiallySelected(docId: number): boolean {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return false;
		const raw = localStorage.getItem(this.storageNameID);
		if (!raw) return false;
		let obj: any;
		try {
			obj = JSON.parse(raw);
		} catch {
			return false;
		}
		const docKey = docId.toString();
		const journals = obj?.[userId]?.[docKey];
		if (!journals || typeof journals !== 'object') return false;
		const values = Object.values(journals);
		if (values.length === 0) return false;
		const allTrue = values.every(v => v === true);
		const allFalse = values.every(v => v === false);
		return !allTrue && !allFalse;
	}



	/**
	 * Ensures a document and its journals exist in storage for the current user, with all journals default checked.
	 * If mode is 'delete', removes the specified journals or the document.
	 * @param docId Document ID
	 * @param journalIds Array of journal IDs
	 * @param mode 'add' (default) or 'delete'
	 */
	ensureDocumentAndJournalsExist(docId: number, journalIds: number[], mode: 'add' | 'delete' = 'add'): void {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return;
		let obj: any = {};
		const raw = localStorage.getItem(this.storageNameID);
		if (raw) {
			try { obj = JSON.parse(raw); } catch { obj = {}; }
		}
		if (!obj[userId]) obj[userId] = {};
		const docKey = docId.toString();
		if (mode === 'add') {
			if (!obj[userId][docKey]) obj[userId][docKey] = {};
			journalIds.forEach(journalId => {
				if (!(journalId.toString() in obj[userId][docKey])) {
					obj[userId][docKey][journalId.toString()] = this.defaultChecked;
				}
			});
		} else if (mode === 'delete') {
			if (obj[userId][docKey]) {
				if (journalIds.length === 0) {
					// Remove the entire document
					delete obj[userId][docKey];
				} else {
					// Remove specified journals
					journalIds.forEach(journalId => {
						delete obj[userId][docKey][journalId.toString()];
					});
					// If document has no journals left, remove the document
					if (Object.keys(obj[userId][docKey]).length === 0) {
						delete obj[userId][docKey];
					}
				}
			}
		}
		localStorage.setItem(this.storageNameID, JSON.stringify(obj));
	}

	/**
	 * Ensures a single journal exists for a document for the current user, default checked.
	 */
	ensureJournalExists(docId: number, journalId: number): void {
		this.ensureDocumentAndJournalsExist(docId, [journalId]);
	}

	/**
	 * Adds a checked pseudo journal for the document only if it currently has no journals at all,
	 * so journal-less documents remain selectable/deselectable like any other document.
	 */
	ensurePseudoJournalForJournalLessDocument(docId: number): void {
		if (this.getAllJournalsForDocument(docId).length === 0) {
			this.ensureDocumentAndJournalsExist(docId, [this.pseudoJournalId]);
		}
	}

	isPseudoJournalId(journalId: number): boolean {
		return journalId === this.pseudoJournalId;
	}

	/**
	 * True when the document has no real (non-pseudo) journals, i.e. it can never be sent to movein.
	 */
	isJournalLessDocument(docId: number): boolean {
		const journalIds = this.getAllJournalsForDocument(docId);
		return journalIds.length === 0 || journalIds.every(id => this.isPseudoJournalId(id));
	}

	/**
	 * Returns true if the journal is checked for the current user and document.
	 * Default is true (checked) if not set, because defaultChecked=true.
	 */
	isJournalChecked(docId: number, journalId: number): boolean
	{
		if(!docId || !journalId) return false;
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return true;
		const raw = localStorage.getItem(this.storageNameID);
		if (!raw) return true;
		let obj: any;
		try { obj = JSON.parse(raw); } catch { return true; }
		const docKey = docId.toString();
		// If not set, defaultChecked=true means checked
		return obj?.[userId]?.[docKey]?.[journalId.toString()] !== false;
	}

	/**
	 * Returns true if all journals for the document are checked for the current user.
	 * Default is true (checked) if not set, because defaultChecked=true.
	 */
	isDocumentChecked(docId: number): boolean {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return true;
		const raw = localStorage.getItem(this.storageNameID);
		if (!raw) return true;
		let obj: any;
		try { obj = JSON.parse(raw); } catch { return true; }
		const docKey = docId.toString();
		const journals = obj?.[userId]?.[docKey];
		if (!journals || typeof journals !== 'object') return true;
		const values = Object.values(journals);
		if (values.length === 0) return true;
		// If not set, defaultChecked=true means checked
		return values.every(v => v === true);
	}

	/**
	 * Returns an array of journalIds that are checked for the document and user.
	 */
	getCheckedJournals(docId: number): number[] {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return [];
		const raw = localStorage.getItem(this.storageNameID);
		if (!raw) return [];
		let obj: any;
		try { obj = JSON.parse(raw); } catch { return []; }
		const docKey = docId.toString();
		const journals = obj?.[userId]?.[docKey];
		if (!journals || typeof journals !== 'object') return [];
		return Object.keys(journals).filter(jid => journals[jid] === true).map(jid => parseInt(jid, 10));
	}

	/**
	 * Returns all journalIds for a document for the current user.
	 */
	getAllJournalsForDocument(docId: number): number[] {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return [];
		const raw = localStorage.getItem(this.storageNameID);
		if (!raw) return [];
		let obj: any;
		try { obj = JSON.parse(raw); } catch { return []; }
		const docKey = docId.toString();
		const journals = obj?.[userId]?.[docKey];
		if (!journals || typeof journals !== 'object') return [];
		return Object.keys(journals).map(jid => parseInt(jid, 10));
	}

	/**
	 * Returns an array of objects for all journals of a document for the current user, each with id and status.
	 * Example: [{journalId: 121, checked: true}, ...]
	 */
	getJournalsWithStatuses(docId: number): { journalId: number, checked: boolean }[] {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return [];
		const raw = localStorage.getItem(this.storageNameID);
		if (!raw) return [];
		let obj: any;
		try { obj = JSON.parse(raw); } catch { return []; }
		const docKey = docId.toString();
		const journals = obj?.[userId]?.[docKey];
		if (!journals || typeof journals !== 'object') return [];
		const result = Object.keys(journals).map(jid => ({
			journalId: parseInt(jid, 10),
			checked: journals[jid] === true // fallback: treat anything not strictly true as false
		}));
		console.log(`[getJournalsWithStatuses] docId=${docId}, userId=${userId}, journals=`, journals, 'result:', result);
		return result;
	}

	/**
	 * Ensures all journals for a document exist for the current user, default checked.
	 */
	ensureAllJournalsForDocument(docId: number, journalIds: number[]): void {
		this.ensureDocumentAndJournalsExist(docId, journalIds);
	}


	/**
	 * Synchronizes DJWS for the current user with the provided doc->journal map from API.
	 * Removes documents/journals not in API, adds missing ones, keeps checked state for existing.
	 * @param docJournalMap { [docId: number]: number[] }
	 */
	syncDJWSWithApi(docJournalMap: { [docId: number]: number[] }): void {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return;
		let obj: any = {};
		const raw = localStorage.getItem(this.storageNameID);
		if (raw) {
			try { obj = JSON.parse(raw); } catch { obj = {}; }
		}
		if (!obj[userId]) obj[userId] = {};
		// Remove documents not in API
		Object.keys(obj[userId]).forEach(docKey => {
			const docId = parseInt(docKey, 10);
			if (!(docId in docJournalMap)) {
				delete obj[userId][docKey];
			}
		});
		// For each document in API
		Object.entries(docJournalMap).forEach(([docIdStr, apiJournalIds]) => {
			// Always (re-)create the doc entry as an object
			obj[userId][docIdStr] = obj[userId][docIdStr] && typeof obj[userId][docIdStr] === 'object' ? obj[userId][docIdStr] : {};
			// Remove journals not in API
			Object.keys(obj[userId][docIdStr]).forEach(jid => {
				if (!apiJournalIds.includes(parseInt(jid, 10))) {
					delete obj[userId][docIdStr][jid];
				}
			});
			// Add missing journals from API (default checked)
			apiJournalIds.forEach(jid => {
				if (!(jid.toString() in obj[userId][docIdStr])) {
					obj[userId][docIdStr][jid.toString()] = this.defaultChecked;
				}
			});
		});
		localStorage.setItem(this.storageNameID, JSON.stringify(obj));
	}


	/**
	 * Removes documents with no journals for the current user from DJWS.
	 * Logs each removal and the final state.
	 */
	removeDocumentsWithNoJournals(): void {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) {
			console.warn('[removeDocumentsWithNoJournals] No userId found.');
			return;
		}
		let obj: any = {};
		const raw = localStorage.getItem(this.storageNameID);
		if (raw) {
			try { obj = JSON.parse(raw); } catch { obj = {}; }
		}
		if (!obj[userId]) {
			console.warn(`[removeDocumentsWithNoJournals] No DJWS for user ${userId}`);
			return;
		}
		const docKeys = Object.keys(obj[userId]);
		let removed = false;
		docKeys.forEach(docKey => {
			const journals = obj[userId][docKey];
			if (!journals || typeof journals !== 'object' || Object.keys(journals).length === 0) {
				console.log(`[removeDocumentsWithNoJournals] Removing document ${docKey} (no journals)`);
				delete obj[userId][docKey];
				removed = true;
			}
		});
		if (removed) {
			localStorage.setItem(this.storageNameID, JSON.stringify(obj));
			console.log('[removeDocumentsWithNoJournals] Updated DJWS:', obj[userId]);
		} else {
			console.log('[removeDocumentsWithNoJournals] No empty documents found.');
		}
	}


	/**
	 * Removes a document for the current user by document id from DJWS.
	 * @param docId Document ID to remove
	 */
	removeDocumentById(docId: number): void {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) {
			console.warn('[removeDocumentById] No userId found.');
			return;
		}
		let obj: any = {};
		const raw = localStorage.getItem(this.storageNameID);
		if (raw) {
			try { obj = JSON.parse(raw); } catch { obj = {}; }
		}
		if (!obj[userId]) {
			console.warn(`[removeDocumentById] No DJWS for user ${userId}`);
			return;
		}
		const docKey = docId.toString();
		if (obj[userId].hasOwnProperty(docKey)) {
			delete obj[userId][docKey];
			localStorage.setItem(this.storageNameID, JSON.stringify(obj));
			console.log(`[removeDocumentById] Removed document ${docKey} for user ${userId}.`);
		} else {
			console.log(`[removeDocumentById] Document ${docKey} not found for user ${userId}.`);
		}
	}


	public getStorageName()
	{
		return this.storageNameID;
	}



	
	/**
	 * Sets the last viewed document and journal for the current user in sessionStorage.
	 * @param documentId Document ID
	 * @param journalId Journal ID
	 */
	setLastViewed(documentId: number, journalId: number): void {
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return;
		let obj: any = {};
		const raw = sessionStorage.getItem(this.lastViewedID);
		if (raw) {
			try { obj = JSON.parse(raw); } catch { obj = {}; }
		}
		obj[userId] = { documentId, journalId };
		sessionStorage.setItem(this.lastViewedID, JSON.stringify(obj));
	}

	/**
	 * Gets the last viewed document and journal for the current user from sessionStorage.
	 * @returns An object { documentId, journalId } or null if not found
	 */
	getLastViewed(): { documentId: number, journalId: number } | null 
	{
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return null;

		const raw = sessionStorage.getItem(this.lastViewedID);
		if (!raw) return null;

		let obj: any;
		try 
		{
			obj = JSON.parse(raw); 
		}
		catch
		{ 
			return null; 
		}
	
		return (obj && obj[userId]) ? obj[userId] : null;
	}


	/**
	 * Removes the last viewed document and journal for the current user from sessionStorage.
	 */
	removeLastViewed(): void 
	{
		const userId = this.viewScopeService.getCurrentCustomer();
		if (!userId) return;

		const raw = sessionStorage.getItem(this.lastViewedID);
		if (!raw) return;

		let obj: any;
		try 
		{
			obj = JSON.parse(raw);
		}
		catch
		{
			return; 
		}

		if (obj && obj.hasOwnProperty(userId)) 
		{
			delete obj[userId];
			sessionStorage.setItem(this.lastViewedID, JSON.stringify(obj));
		}
	}
}